/**
 * Loading Spinner Component
 *  */

import { useTranslation } from 'react-i18next';
import { useMemeLoaderForUi } from '@/providers/MemeLoaderPreferenceProvider';
import { cn } from '@/lib/utils';

import {
  MemeMediaView,
  MEME_GIF_URLS,
  useRandomMemeMediaIndex,
  type MemeMediaLayout,
} from './meme-loader';

interface LoadingSpinnerProps {
  size?: 'xs' | 'sm' | 'md' | 'lg';
  className?: string;
}

const SPINNER_MEME_LAYOUT: Record<NonNullable<LoadingSpinnerProps['size']>, MemeMediaLayout> = {
  xs: 'inline-xs',
  sm: 'inline-sm',
  md: 'inline-md',
  lg: 'inline-lg',
};

function ClassicSpinner({
  size,
  className,
}: Required<Pick<LoadingSpinnerProps, 'size'>> & Pick<LoadingSpinnerProps, 'className'>) {
  const { t } = useTranslation('common');
  const sizeClasses = {
    xs: 'h-3 w-3 border',
    sm: 'h-4 w-4',
    md: 'h-8 w-8',
    lg: 'h-12 w-12',
  };

  return (
    <div
      className={cn(
        'animate-spin rounded-full border-muted border-t-primary',
        size === 'xs' ? 'border' : 'border-2',
        sizeClasses[size],
        className,
      )}
      role="status"
    >
      <span className="sr-only">{t('status.loading')}</span>
    </div>
  );
}

export function LoadingSpinner({ size = 'md', className }: LoadingSpinnerProps) {
  const { memeLoaderEnabled } = useMemeLoaderForUi();
  const mediaIndex = useRandomMemeMediaIndex(MEME_GIF_URLS.length);

  if (memeLoaderEnabled) {
    return (
      <MemeMediaView
        src={MEME_GIF_URLS[mediaIndex]}
        layout={SPINNER_MEME_LAYOUT[size]}
        className={className}
        labelled
      />
    );
  }

  return <ClassicSpinner size={size} className={className} />;
}
